import { makeStyles } from "tss-react/mui"
import { Grid, Stack, Typography, Chip } from "@mui/material"
import { DateTime } from "luxon"
import { Box } from "@mui/system"
import {
    fetchPosOrders,
    fetchPosOrdersForExport,
    PosOrdersForExportResponse,
    PosOrdersResponse,
    PosDeliveryListQuery,
} from "../posDeliveryListApi"
import { useMemo, useState } from "react"
import { SelectDateRange } from "../../meetingService/components/deliveryListModal/SelectDateRange"
import PosSingleOrder from "../components/PosSingleOrder"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ErrorView } from "../../../../../../shared/components/errors/ErrorView"
import { DateFilterParams } from "../../../../../../shared/components/table/useInitialDateFilters"
import { SharePrintButtons } from "../../../../../nonAdminUser/reports/MeetingsReceipt/components/SharePrintButtons"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { DeliveryListHeader } from "../../meetingService/components/deliveryListModal/DeliveryListHeader"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import GeneralErrorAnim from "../../../../../../shared/assets/general-error-lottie-anim.json"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { Order } from "../../../../controlKitchenTypes"
import { getDateKey } from "../../meetingService/components/deliveryListModal/helpers"

const useStyles = makeStyles()((theme) => ({
    container: {
        backgroundColor: tailwindPalette.slate[100],
        minHeight: "100vh",
        gap: "3.5rem",
        paddingBottom: "3.5rem",
        justifyContent: "start",
        [theme.breakpoints.down("sm")]: {
            backgroundColor: theme.palette.common.white,
            gap: 0,
        },
        "@media print": {
            backgroundColor: "transparent",
            minHeight: "auto",
            width: "100vw",
            paddingBottom: 0,
        },
    },
    slideContainer: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
    },
    slide: {
        width: "80%",
        backgroundColor: "white",
        boxShadow: "0px 0px 10px 0px #00000026;",
        paddingBottom: "3.5rem",
        "@media print": {
            width: "100%",
            display: "block",
            boxShadow: "none",
            backgroundColor: "white",
        },
    },
    header: {
        padding: "1.25rem 1.5rem",
        position: "sticky",
        top: 0,
        maxHeight: "4.5rem",
        backgroundColor: "white",
        boxShadow: "0px 2px 6px 0px #00000014",
        "@media print": {
            display: "none",
        },
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
            padding: "0 1.5rem",
            zIndex: 1,
            maxHeight: "auto",
            boxShadow: "0px 3px 1px -2px #00000033,  0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F",
        },
    },
    actionButtons: {
        justifySelf: "end",
        flex: "none",
    },
    emptyState: {
        textAlign: "center",
        padding: "4rem 2rem",
    },
    dateHeader: {
        color: "white",
        background: "rgba(0, 0, 0, 0.87)",
        margin: "0 3.5rem",
        padding: "0 .5rem",
        "@media print": {
            padding: 0,
            margin: 0,
        },
    },
    ordersContainer: {
        padding: "0 3.5rem",
        "@media print": {
            padding: 0,
        },
    },
    orderDivider: {
        borderStyle: "dashed",
        margin: "0 3.5rem",
        borderColor: theme.palette.tailwind.slate[700],
    },
}))

export type DeliveryListMode = "selected" | "all"
export type DeliveryListDateState = {
    startDate: DateTime | null
    endDate: DateTime | null
}

export type PosOrdersDependencies = Readonly<{
    merchantId: number
    startDeliveryDate?: string
    endDeliveryDate?: string
    shopIds?: string
    orderType?: string
}>

type PosDeliveryListProps = Readonly<{
    open: boolean
    onClose: () => void
    mode: DeliveryListMode
    orderIds?: number[]
    dependencies: PosOrdersDependencies
}>

const transformOrdersForExport = (data: PosOrdersForExportResponse): Order[] => data?.orders
const selectPosOrders = (data: PosOrdersResponse): Order[] => data?.orders

export function PosDeliveryList({ mode, orderIds, dependencies, open, onClose }: PosDeliveryListProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { startDeliveryDate, endDeliveryDate, ...rest } = dependencies
    const initStartDate = startDeliveryDate
    const initEndDate = endDeliveryDate
    const userId = useAppSelector(selectEffectiveUserId) || 0

    const isAuthenticated = Boolean(userId)
    const isSelectMode = mode === "selected"

    const dateTimeFactory = useDateTime()
    const [dateState, setDateState] = useState<DeliveryListDateState>({
        startDate: initStartDate ? dateTimeFactory.fromISO(initStartDate) : null,
        endDate: initEndDate ? dateTimeFactory.fromISO(initEndDate) : null,
    })
    const [hasCustomDateRange, setHasCustomDateRange] = useState(false)
    // No dates from the table ("All") and none picked here: the list covers every delivery date.
    const showPeriodAll = !hasCustomDateRange && !initStartDate && !initEndDate

    const {
        data: exportOrders = [],
        isFetching: isFetchingOrdersForExport,
        error: exportOrdersError,
    } = useApiQuery(fetchPosOrdersForExport, {
        queryName: PosDeliveryListQuery.POS_ORDERS_FOR_EXPORT,
        dependencies: {
            orderIds,
        },
        enabled: isAuthenticated && isSelectMode && Boolean(orderIds?.length),
        select: transformOrdersForExport,
    })

    const {
        data: allOrders = [],
        isFetching: isFetchingAllOrders,
        error: allOrdersError,
    } = useApiQuery(fetchPosOrders, {
        queryName: PosDeliveryListQuery.POS_ORDERS,
        dependencies: {
            merchantId: rest.merchantId,
            startDate: dateState.startDate?.toISODate() ?? undefined,
            endDate: dateState.endDate?.toISODate() ?? undefined,
            shopIds: rest.shopIds,
            orderType: rest.orderType,
        },
        enabled: isAuthenticated && !isSelectMode,
        select: selectPosOrders,
    })

    const orders = isSelectMode ? exportOrders : allOrders
    const isFetching = isSelectMode ? isFetchingOrdersForExport : isFetchingAllOrders
    const loadError = isSelectMode ? exportOrdersError : allOrdersError

    const groupedByDate = useMemo(() => {
        const grouped: Record<string, Order[]> = {}

        for (const order of orders) {
            const deliveryTime = order.deliveries?.[0]?.deliveryTime
            if (!deliveryTime) continue

            const dt = dateTimeFactory.fromApi(deliveryTime)
            if (!dt.isValid) continue

            const dateKey = getDateKey(dt)
            if (!grouped[dateKey]) {
                grouped[dateKey] = []
            }
            grouped[dateKey].push(order)
        }

        Object.keys(grouped).forEach((dateKey) => {
            grouped[dateKey].sort((a, b) => {
                const timeA = a.deliveries?.[0]?.deliveryTime || ""
                const timeB = b.deliveries?.[0]?.deliveryTime || ""
                return dateTimeFactory.fromISO(timeA).toMillis() - dateTimeFactory.fromISO(timeB).toMillis()
            })
        })

        return grouped
    }, [orders, dateTimeFactory])

    const hasNoOrders = !isFetching && !loadError && orders.length === 0

    const content = loadError ? (
        <ErrorView
            lottieAnimation={GeneralErrorAnim}
            title="A problem occurred"
            message="Failed to load"
            buttonLabel="Close"
            onClose={onClose}
        />
    ) : (
        <Stack direction="column" className={classes.container}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" className={classes.header}>
                <SelectDateRange
                    value={dateState}
                    onChange={(newValue) => {
                        setDateState(newValue)
                        setHasCustomDateRange(true)
                    }}
                    paramNameStart={DateFilterParams.START}
                    paramNameEnd={DateFilterParams.END}
                    showPeriodAll={showPeriodAll}
                />

                <Box className={classes.actionButtons}>
                    <SharePrintButtons onClose={onClose} hasShare={false} hasClose={true} />
                </Box>
            </Stack>
            {isFetching ? (
                <Grid container direction="column" component="main" alignItems="center" justifyContent="center">
                    <LoadingSpinner />
                </Grid>
            ) : (
                <Box id="printable-content" className={classes.slideContainer}>
                    <Stack direction="column" className={classes.slide}>
                        <DeliveryListHeader
                            dateState={dateState}
                            label={l10n.getString("pos-order-delivery-date-title")}
                            showPeriodAll={showPeriodAll}
                        />

                        {hasNoOrders ? (
                            <Box className={classes.emptyState}>
                                <LocalizedStrict id="meeting-service-delivery-list-empty">
                                    <Typography variant="h6">No delivery orders for the selected criteria</Typography>
                                </LocalizedStrict>
                            </Box>
                        ) : (
                            <Stack gap="4rem">
                                {Object.entries(groupedByDate)
                                    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                                    .map(([dateKey, dateOrders]) => (
                                        <Stack gap="1.5rem" key={dateKey}>
                                            <Box>
                                                <Chip
                                                    label={
                                                        <Typography variant="h6" fontWeight={500}>
                                                            {dateTimeFactory.toDate(
                                                                dateTimeFactory.fromISO(`${dateKey}T00:00:00`),
                                                            )}
                                                        </Typography>
                                                    }
                                                    className={classes.dateHeader}
                                                />
                                            </Box>

                                            <Stack gap="2.25rem" className={classes.ordersContainer}>
                                                {dateOrders.map((order, index) => (
                                                    <PosSingleOrder
                                                        key={order.id}
                                                        order={order}
                                                        itemIndex={index}
                                                        totalOrders={dateOrders.length}
                                                    />
                                                ))}
                                            </Stack>
                                        </Stack>
                                    ))}
                            </Stack>
                        )}
                    </Stack>
                </Box>
            )}
        </Stack>
    )

    return (
        <StandardDialog open={open} onClose={onClose} isFullScreen hideCloseIcon topbar={<></>}>
            {content}
        </StandardDialog>
    )
}
